import React, { useState, useMemo } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';
import InfoTooltip from './InfoTooltip';

const PAY_TYPES = [
  { value: 'ssp_only', label: 'SSP Only' },
  { value: 'company_sick_pay', label: 'Company Sick Pay' },
  { value: 'manual', label: 'Custom / Manual' },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function weeksAndDaysBetween(start, end) {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (e <= s) return null;
  const diffMs = e - s;
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  return { weeks, days, totalDays };
}

// Build a duration object from a list of YYYY-MM-DD strings.
// Used for the new actual-days picker so tier/banner numbers reflect days off, not elapsed time.
function durationFromActualDays(actualDays) {
  if (!Array.isArray(actualDays) || actualDays.length === 0) return null;
  const totalDays = actualDays.length;
  const weeks = Math.floor(totalDays / 5); // working-week weeks
  const days = totalDays % 5;
  return { weeks, days, totalDays };
}

function formatDuration(dur) {
  if (!dur) return '';
  const parts = [];
  if (dur.weeks > 0) parts.push(`${dur.weeks} week${dur.weeks !== 1 ? 's' : ''}`);
  if (dur.days > 0) parts.push(`${dur.days} day${dur.days !== 1 ? 's' : ''}`);
  return parts.join(', ') || '0 days';
}

export default function SickModal({ employee, existingSickRecord, onSave, onClose }) {
  useEscapeKey(onClose);

  const isUpdate = !!existingSickRecord;

  const [form, setForm] = useState(() => {
    if (existingSickRecord) {
      const existingActualDays = Array.isArray(existingSickRecord.actual_sick_days)
        ? existingSickRecord.actual_sick_days
        : [];
      return {
        start_date: existingSickRecord.start_date || '',
        actual_sick_days: existingActualDays,
        return_date: existingSickRecord.actual_return_date || '',
        sick_pay_type: existingSickRecord.sick_pay_type || 'ssp_only',
        csp_full_pay_weeks: existingSickRecord.csp_full_pay_weeks || '',
        csp_half_pay_weeks: existingSickRecord.csp_half_pay_weeks || '',
        manual_instructions: existingSickRecord.manual_instructions || '',
        fit_note_received: existingSickRecord.fit_note_received || false,
        fit_note_end_date: existingSickRecord.fit_note_end_date || '',
        notes: existingSickRecord.notes || '',
        status: existingSickRecord.status || 'active',
      };
    }
    return {
      start_date: '',
      actual_sick_days: [],
      return_date: '',
      sick_pay_type: 'ssp_only',
      csp_full_pay_weeks: '',
      csp_half_pay_weeks: '',
      manual_instructions: '',
      fit_note_received: false,
      fit_note_end_date: '',
      notes: '',
      status: 'active',
    };
  });

  // Calendar grid state for the actual-days picker
  const initialPickerDate = (() => {
    const seed = existingSickRecord?.start_date || new Date().toISOString().split('T')[0];
    const d = new Date(seed);
    return { month: d.getMonth(), year: d.getFullYear() };
  })();
  const [pickerMonth, setPickerMonth] = useState(initialPickerDate.month);
  const [pickerYear, setPickerYear] = useState(initialPickerDate.year);

  const navigatePickerMonth = (delta) => {
    let m = pickerMonth + delta;
    let y = pickerYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setPickerMonth(m);
    setPickerYear(y);
  };

  const toggleSickDay = (dateStr) => {
    setForm(prev => {
      const dates = prev.actual_sick_days.includes(dateStr)
        ? prev.actual_sick_days.filter(d => d !== dateStr)
        : [...prev.actual_sick_days, dateStr].sort();
      // Keep start_date as the earliest selected day if not already locked-in
      const earliest = dates[0] || '';
      const next = { ...prev, actual_sick_days: dates };
      if (!isUpdate || !prev.start_date) next.start_date = earliest;
      return next;
    });
  };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError('');
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Absence duration: prefer the actual-days count when the picker has been used,
  // fall back to elapsed-time calc for legacy records that pre-date the picker.
  const today = new Date().toISOString().split('T')[0];
  const elapsed = useMemo(() => {
    const fromActual = durationFromActualDays(form.actual_sick_days);
    if (fromActual) return fromActual;
    return weeksAndDaysBetween(form.start_date, today);
  }, [form.actual_sick_days, form.start_date, today]);

  const currentTier = useMemo(() => {
    if (form.sick_pay_type !== 'company_sick_pay' || !elapsed) return null;
    const fullWeeks = parseInt(form.csp_full_pay_weeks) || 0;
    const halfWeeks = parseInt(form.csp_half_pay_weeks) || 0;
    const elapsedWeeks = elapsed.weeks + (elapsed.days > 0 ? 1 : 0); // round up partial weeks

    if (elapsedWeeks <= fullWeeks) {
      return { tier: 'full_pay', label: 'Full Pay', weeksRemaining: fullWeeks - elapsed.weeks };
    } else if (elapsedWeeks <= fullWeeks + halfWeeks) {
      return { tier: 'half_pay', label: 'Half Pay', weeksRemaining: (fullWeeks + halfWeeks) - elapsed.weeks };
    } else {
      return { tier: 'ssp', label: 'SSP Only', weeksRemaining: 0 };
    }
  }, [form.sick_pay_type, form.csp_full_pay_weeks, form.csp_half_pay_weeks, elapsed]);

  // SSP rules changed from 6 April 2026 — waiting days abolished, payable from day 1.
  // Keep the historical calculator only for absences that started before then.
  const SSP_REFORM_DATE = '2026-04-06';
  const isPostReform = form.start_date && form.start_date >= SSP_REFORM_DATE;

  const sspInfo = useMemo(() => {
    if (!form.start_date) return null;
    if (form.start_date >= SSP_REFORM_DATE) return null; // day-1 SSP, no waiting days
    const start = new Date(form.start_date);
    // Count 3 working days (Mon-Fri) from start
    let workingDays = 0;
    let current = new Date(start);
    while (workingDays < 3) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) workingDays++;
      if (workingDays < 3) current.setDate(current.getDate() + 1);
    }
    const waitingEnd = new Date(current);
    const sspStart = new Date(waitingEnd);
    sspStart.setDate(sspStart.getDate() + 1);
    // Skip to next working day if needed
    while (sspStart.getDay() === 0 || sspStart.getDay() === 6) {
      sspStart.setDate(sspStart.getDate() + 1);
    }
    return { waitingEnd, sspStart };
  }, [form.start_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isUpdate && !form.start_date && form.actual_sick_days.length === 0) {
      setError('Select at least one sick day on the calendar');
      return;
    }
    if (form.return_date && form.start_date && new Date(form.return_date) < new Date(form.start_date)) {
      setError('Return date cannot be before the start date');
      return;
    }
    if (form.sick_pay_type === 'company_sick_pay') {
      if (!form.csp_full_pay_weeks && !form.csp_half_pay_weeks) {
        setError('Please enter at least one pay tier duration');
        return;
      }
    }
    if (form.sick_pay_type === 'manual' && !form.manual_instructions) {
      setError('Please provide instructions for the manual pay adjustment');
      return;
    }

    setSaving(true);
    try {
      const saveData = {
        ...form,
        sick_pay_type_label: PAY_TYPES.find(t => t.value === form.sick_pay_type)?.label || form.sick_pay_type,
      };

      // Include calculated info for payroll provider
      if (elapsed) {
        saveData.absence_duration = formatDuration(elapsed);
        saveData.elapsed_weeks = elapsed.weeks;
      }
      if (currentTier) {
        saveData.current_pay_tier = currentTier.label;
      }

      if (form.return_date) {
        saveData.status = 'returned';
      }

      if (isUpdate) {
        saveData.is_update = true;
        saveData.sick_record_id = existingSickRecord.id;
      }

      await onSave(employee.id, saveData);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const tierColorClass = currentTier?.tier === 'full_pay'
    ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
    : currentTier?.tier === 'half_pay'
      ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
      : 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isUpdate ? 'Update Sick Leave' : 'Record Sick Leave'} - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {/* Existing absence banner */}
          {isUpdate && elapsed && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Ongoing absence — {formatDuration(elapsed)}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Off since {new Date(form.start_date).toLocaleDateString('en-GB')}. Update details below or record a return.
              </p>
              {currentTier && (
                <div className={`mt-2 text-xs font-medium px-2 py-1 rounded border inline-block ${tierColorClass}`}>
                  Currently: {currentTier.label}
                  {currentTier.weeksRemaining > 0 && ` (${currentTier.weeksRemaining} week${currentTier.weeksRemaining !== 1 ? 's' : ''} remaining)`}
                </div>
              )}
            </div>
          )}

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First day of absence — derived from the day picker for new records */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                First Day of Absence
                <InfoTooltip text="Earliest sick day. For new records this is set automatically from the calendar selection below." />
              </label>
              {form.start_date ? (
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-medium">
                  {new Date(form.start_date).toLocaleDateString('en-GB')}
                </p>
              ) : (
                <p className="mt-1 text-sm text-gray-400">— select sick days below —</p>
              )}
            </div>

            {/* SSP waiting days — only shown for absences that started before the 6 April 2026 reform */}
            {sspInfo && !isUpdate && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm space-y-1">
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">3 waiting days:</span>{' '}
                  {new Date(form.start_date).toLocaleDateString('en-GB')} – {sspInfo.waitingEnd.toLocaleDateString('en-GB')}
                  <InfoTooltip text="Pre-6 April 2026 absences only — SSP was not paid for the first 3 qualifying days (Mon–Fri)." />
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">SSP payable from:</span>{' '}
                  {sspInfo.sspStart.toLocaleDateString('en-GB')}
                </p>
              </div>
            )}

            {/* Post-reform: day-1 SSP info */}
            {isPostReform && !isUpdate && (
              <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg text-sm">
                <p className="text-emerald-800 dark:text-emerald-300">
                  <span className="font-medium">SSP payable from day 1.</span>{' '}
                  Waiting days were abolished in the 6 April 2026 reform.
                  <InfoTooltip text="From 6 April 2026, the 3 waiting-day rule and the Lower Earnings Limit threshold for SSP were removed. SSP is now payable from the first qualifying day for all eligible employees." />
                </p>
              </div>
            )}

            {/* Sick Pay Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Sick Pay Arrangement
                <InfoTooltip text="How should this employee be paid during their absence? SSP Only is the statutory minimum. Company Sick Pay means your organisation tops up or replaces SSP." />
              </label>
              <div className="mt-2 space-y-2">
                {PAY_TYPES.map(pt => (
                  <label key={pt.value} className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                    form.sick_pay_type === pt.value
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-400'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}>
                    <input
                      type="radio"
                      name="sick_pay_type"
                      value={pt.value}
                      checked={form.sick_pay_type === pt.value}
                      onChange={handleChange}
                      className="mt-0.5 text-brand-600 focus:ring-brand-500"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{pt.label}</span>
                      {pt.value === 'ssp_only' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Statutory Sick Pay only — payable from day 1 (absences from 6 April 2026)
                        </p>
                      )}
                      {pt.value === 'company_sick_pay' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Enhanced sick pay — specify full pay and half pay durations below
                        </p>
                      )}
                      {pt.value === 'manual' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Bespoke arrangement — provide specific pay instructions
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Company Sick Pay tiers */}
            {form.sick_pay_type === 'company_sick_pay' && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Company Sick Pay Tiers
                  <InfoTooltip text="Enter the number of weeks at each pay level. After these tiers are exhausted the employee drops to SSP only. Leave a tier blank or 0 if it doesn't apply." />
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
                    <label className="text-sm text-gray-700 dark:text-gray-300 w-20">Full pay</label>
                    <input
                      type="number"
                      name="csp_full_pay_weeks"
                      min="0"
                      max="52"
                      value={form.csp_full_pay_weeks}
                      onChange={handleChange}
                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                      placeholder="0"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">weeks</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-amber-500 flex-shrink-0" />
                    <label className="text-sm text-gray-700 dark:text-gray-300 w-20">Half pay</label>
                    <input
                      type="number"
                      name="csp_half_pay_weeks"
                      min="0"
                      max="52"
                      value={form.csp_half_pay_weeks}
                      onChange={handleChange}
                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                      placeholder="0"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">weeks</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-400 flex-shrink-0" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Then SSP only</span>
                  </div>
                </div>

                {/* Visual timeline */}
                {(parseInt(form.csp_full_pay_weeks) > 0 || parseInt(form.csp_half_pay_weeks) > 0) && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Pay timeline</p>
                    <div className="flex rounded-full overflow-hidden h-3">
                      {parseInt(form.csp_full_pay_weeks) > 0 && (
                        <div
                          className="bg-green-500 flex items-center justify-center"
                          style={{ flex: parseInt(form.csp_full_pay_weeks) }}
                          title={`Full pay: ${form.csp_full_pay_weeks} weeks`}
                        />
                      )}
                      {parseInt(form.csp_half_pay_weeks) > 0 && (
                        <div
                          className="bg-amber-500 flex items-center justify-center"
                          style={{ flex: parseInt(form.csp_half_pay_weeks) }}
                          title={`Half pay: ${form.csp_half_pay_weeks} weeks`}
                        />
                      )}
                      <div
                        className="bg-red-300 dark:bg-red-400/50 flex items-center justify-center"
                        style={{ flex: Math.max(2, (parseInt(form.csp_full_pay_weeks) || 0) + (parseInt(form.csp_half_pay_weeks) || 0)) }}
                        title="SSP only"
                      />
                    </div>
                    <div className="flex text-[10px] text-gray-400 dark:text-gray-500 mt-1 justify-between">
                      <span>Week 1</span>
                      {parseInt(form.csp_full_pay_weeks) > 0 && parseInt(form.csp_half_pay_weeks) > 0 && (
                        <span>Week {parseInt(form.csp_full_pay_weeks) + 1}</span>
                      )}
                      <span>Week {(parseInt(form.csp_full_pay_weeks) || 0) + (parseInt(form.csp_half_pay_weeks) || 0) + 1}+</span>
                    </div>
                  </div>
                )}

                {/* Current position indicator for updates */}
                {isUpdate && currentTier && (
                  <div className={`text-sm rounded p-3 border ${tierColorClass}`}>
                    <span className="font-medium">This pay period:</span> {currentTier.label}
                    {currentTier.weeksRemaining > 0 && (
                      <span> — {currentTier.weeksRemaining} week{currentTier.weeksRemaining !== 1 ? 's' : ''} remaining at this level</span>
                    )}
                    {currentTier.tier === 'ssp' && (
                      <span> — company sick pay tiers exhausted</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Manual instructions */}
            {form.sick_pay_type === 'manual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Pay Instructions *
                  <InfoTooltip text="Tell the payroll provider exactly what to pay for this absence — e.g. 'Pay full salary for first 4 weeks, then 80% for weeks 5–8, then SSP'." />
                </label>
                <textarea name="manual_instructions" rows="3" value={form.manual_instructions} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                  placeholder="e.g. Pay full salary for the first month, then revert to SSP..." />
              </div>
            )}

            {/* Actual sick days picker */}
            {!form.return_date && (() => {
              const daysInMonth = getDaysInMonth(pickerYear, pickerMonth);
              const firstDayOfWeek = new Date(pickerYear, pickerMonth, 1).getDay();
              const cells = [];
              for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) cells.push(d);
              return (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sick Days *
                    <InfoTooltip text="Tick each day the employee was off sick. Use this for retrospective recording — e.g. if they were off for the first half of the month and have now returned." />
                  </label>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <button type="button" onClick={() => navigatePickerMonth(-1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" aria-label="Previous month">
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {MONTH_NAMES[pickerMonth]} {pickerYear}
                      </span>
                      <button type="button" onClick={() => navigatePickerMonth(1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" aria-label="Next month">
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                        <div key={d} className="text-xs font-medium text-gray-400 py-1">{d}</div>
                      ))}
                      {cells.map((day, idx) => {
                        if (day === null) return <div key={`empty-${idx}`} />;
                        const dateStr = `${pickerYear}-${String(pickerMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const isSelected = form.actual_sick_days.includes(dateStr);
                        const dayOfWeek = new Date(pickerYear, pickerMonth, day).getDay();
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleSickDay(dateStr)}
                            className={`text-xs py-1.5 rounded transition-colors ${
                              isSelected
                                ? 'bg-red-600 text-white font-semibold'
                                : isWeekend
                                  ? 'text-gray-300 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {form.actual_sick_days.length === 0
                        ? 'No days selected.'
                        : `${form.actual_sick_days.length} day${form.actual_sick_days.length === 1 ? '' : 's'} selected${form.start_date ? ` — earliest ${new Date(form.start_date).toLocaleDateString('en-GB')}` : ''}.`}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Return date — primarily for updates */}
            {isUpdate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Actual Return Date
                  <InfoTooltip text="Enter the date the employee returned (or will return) to work. This closes the absence and stops sick pay." />
                </label>
                <DateInput name="return_date" value={form.return_date} onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                {form.return_date && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">
                    This will close the absence and mark the employee as returned.
                  </p>
                )}
              </div>
            )}

            {/* Fit note */}
            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input type="checkbox" name="fit_note_received" checked={form.fit_note_received} onChange={handleChange}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Fit note received
                  <InfoTooltip text="A fit note (Statement of Fitness for Work) from a GP is required for absences longer than 7 calendar days. Tick this once received." />
                </span>
              </label>

              {form.fit_note_received && (
                <div className="ml-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Fit note covers until</label>
                  <DateInput name="fit_note_end_date" value={form.fit_note_end_date} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>
              )}

              {/* Fit note reminder */}
              {form.start_date && !form.fit_note_received && elapsed && elapsed.totalDays >= 7 && (
                <div className="p-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-300">
                  This absence is over 7 days. A fit note from the employee's GP is usually required.
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="2" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Any additional details..." />
            </div>

            {/* Info banner */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm text-blue-700 dark:text-blue-300">
              {isUpdate
                ? `This update for ${employee.first_name} ${employee.last_name}'s sick leave will be included in the next payroll submission.`
                : `This sick leave record for ${employee.first_name} ${employee.last_name} will be included in the next payroll submission. You can update it in future months if the absence continues.`
              }
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving
                  ? 'Saving...'
                  : form.return_date
                    ? 'Record Return'
                    : isUpdate
                      ? 'Update Sick Leave'
                      : 'Record Sick Leave'
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
